import { Link } from '@remix-run/react';
import type { RouteHandle } from '@remix-run/server-runtime';
import { useHydrated } from 'remix-utils/use-hydrated';
import { analytics } from '~/components/analytics/segment-tracker.client.tsx';
import { StudentStoriesSection } from '~/components/landing/animated-student-stories.tsx';
import { FAQSection } from '~/components/landing/faq.tsx';
import { StorageScholarsSection } from '~/components/landing/storage-scholars.tsx';
import { WhatAreYouWaitingForSection } from '~/components/landing/what-are-you-waiting-for.js';
import { Button } from '~/components/ui/button.tsx';
import { Icon } from '~/components/ui/icon.tsx';
import { cn } from '~/utils/misc.ts';
import { useIsAuthenticated } from '~/utils/user.js';
import type { LandingHandle } from './_layout.tsx';

export const handle = {
  mode: 'host',
  mainClass: 'flex flex-col items-center',
} satisfies RouteHandle & LandingHandle;

export default function HostLandingRoute() {
  return (
    <>
      <div className="mx-4 w-full max-w-[1440px] border-gray-100 bg-primary/10 sm:bg-transparent md:mx-8">
        <div className="relative flex min-h-[max(700px,min(1200px,calc(100vh-var(--landing-header-height))))] flex-col items-start border-gray-200 border-b sm:items-center sm:border-b-0 sm:pt-16 md:justify-center md:pt-0 md:pb-40">
          <HeroSection />
          <div className="absolute bottom-0 hidden sm:block">
            <UniversitiesSection />
          </div>
          <div className="py- absolute top-12 right-0 left-0 block overflow-x-hidden overflow-y-visible py-4 sm:hidden">
            <HorizontalUniversitiesSection />
          </div>
        </div>
      </div>
      <StepsSection />
      <StorageScholarsSection />
      <YouCouldEarnSection />
      <StudentStoriesSection />
      <WhatAreYouWaitingForSection className="hidden sm:flex" />
      <WhatAreYouWaitingForSectionMobile />
      <FAQSection />
    </>
  );
}

function HeroSection() {
  const authenticated = useIsAuthenticated();

  return (
    <section className="flex flex-col justify-between pr-4 pb-0 pl-9 sm:pr-4 sm:pl-4">
      <div className="relative flex h-max w-full flex-col items-start pt-48 sm:items-center sm:pt-6">
        <div
          className={cn(
            'flex flex-col items-center pb-3 font-bold text-4xl leading-[normal] sm:text-5xl md:text-6xl lg:w-full lg:text-7xl',
          )}
        >
          <h1 className="text-start sm:text-center">
            Turn your lease <br className="inline sm:hidden" /> into cash
          </h1>
        </div>
        <div
          className={cn(
            'flex w-full flex-col items-start pb-10 text-lg sm:items-center sm:text-2xl',
            'fade-in slide-in-from-bottom animate-in fill-mode-both delay-200 duration-500',
          )}
        >
          <h2 className="text-start sm:text-center">
            Privately sublet to university-verified <br className="inline sm:hidden" /> matches
          </h2>
        </div>
        {!authenticated && (
          <Button
            asChild
            className="fade-in slide-in-from-bottom h-auto animate-in rounded-full fill-mode-both px-16 py-[6px] text-base delay-300 duration-500 lg:text-xl"
          >
            <Link to="/signup?type=host-full" prefetch="intent">
              List now
            </Link>
          </Button>
        )}
        {authenticated && (
          <Button
            asChild
            className="fade-in slide-in-from-bottom h-auto animate-in rounded-full fill-mode-both px-16 py-[6px] text-base delay-300 duration-500 lg:text-xl"
          >
            <Link to="/app/listing/new">List now</Link>
          </Button>
        )}
        <Link
          to="/signup?type=host-partial"
          prefetch="intent"
          className="fade-in slide-in-from-bottom mt-[2px] animate-in fill-mode-both pt-2 text-center text-gray-600 text-sm delay-300 duration-500 sm:mt-0 sm:text-lg"
          onClick={() => {
            analytics.track('Verify Now, List Later Clicked');
          }}
        >
          verify now, list later
        </Link>
      </div>
    </section>
  );
}

function StepsSection() {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-6 px-6 py-8 sm:bg-primary/10 sm:py-12 md:px-8">
      <section className="flex w-full max-w-[1440px] flex-col items-center justify-center gap-8">
        <p className="self-start text-start font-semibold text-2xl sm:text-4xl">How it works</p>
        <div className="grid w-full grid-cols-1 gap-12 sm:grid-cols-2 sm:justify-items-center sm:gap-6 md:justify-items-start lg:grid-cols-4">
          <StepCard title="Sign up" description="Our sign up process takes less than 3 minutes.">
            <img
              src="/img/landing/icons/signup.png"
              alt="signup"
              className="h-8 w-8 min-w-[2rem]"
            />
          </StepCard>
          <StepCard
            title="Get Connected"
            description="We’ll only contact you about verified matched guests."
          >
            <img
              src="/img/landing/icons/connect.png"
              alt="connect"
              className="h-8 w-8 min-w-[2rem]"
            />
          </StepCard>
          <StepCard title="Select" description="Pick only the people you feel comfortable with.">
            <img
              src="/img/landing/icons/select.png"
              alt="select"
              className="h-8 w-8 min-w-[2rem]"
            />
          </StepCard>
          <StepCard title="Get Paid" description="It’s never been easier to make cash!">
            <img
              src="/img/landing/icons/get-paid.png"
              alt="get paid"
              className="h-8 w-8 min-w-[2rem]"
            />
          </StepCard>
        </div>
      </section>
    </div>
  );
}

type StepCardProps = {
  children: React.ReactNode;
  title: string;
  description: string;
  className?: string;
};
function StepCard(props: StepCardProps) {
  const { children, title, description } = props;

  return (
    <div className="flex w-full flex-row gap-4 sm:w-60 sm:flex-col">
      <div className="h-fit w-fit min-w-fit rounded-lg bg-primary/20 p-3">{children}</div>
      <div className="flex w-full flex-col gap-1 sm:gap-4">
        <p className="font-semibold text-xl">{title}</p>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
}

function UniversityList() {
  return (
    <>
      <University universityUrl="/img/landing/universities/harvard-university.png" />
      <University universityUrl="/img/landing/universities/boston-college-eagles.png" />
      <University universityUrl="/img/landing/universities/boston-university.png" />
      <University universityUrl="/img/landing/universities/cal-university.png" />
      <University universityUrl="/img/landing/universities/columbia-university.png" />
      <University universityUrl="/img/landing/universities/fordham-rams.png" />
      <University universityUrl="/img/landing/universities/mit.png" />
      <University universityUrl="/img/landing/universities/new-york-university.png" />
      <University universityUrl="/img/landing/universities/northeastern.png" />
      <University universityUrl="/img/landing/universities/northwestern.png" />
      <University universityUrl="/img/landing/universities/stanford-university.png" />
      <University universityUrl="/img/landing/universities/ucla.png" />
      <University universityUrl="/img/landing/universities/uf-monogram.png" />
      <University universityUrl="/img/landing/universities/university-of-chicago.png" />
      <University universityUrl="/img/landing/universities/university-of-pennsylvania.png" />
      <University universityUrl="/img/landing/universities/usc-trojans.png" />
    </>
  );
}

function HorizontalUniversitiesSection() {
  const hydrated = useHydrated();

  return (
    <div
      className={cn(
        'flex w-max gap-5 overflow-visible [animation-duration:18000ms]',
        hydrated ? 'animate-infinite-scroll-horizontal' : '-translate-x-1/4',
      )}
    >
      <UniversityList />
      <UniversityList />
    </div>
  );
}

function UniversitiesSection() {
  return (
    <div className="grid grid-cols-[repeat(3,minmax(0,1fr))] gap-6 overflow-hidden px-4 py-4 md:grid-cols-[repeat(5,minmax(0,1fr))] lg:grid-cols-[repeat(7,minmax(0,1fr))] xl:grid-cols-[repeat(9,minmax(0,1fr))]">
      <div className="hidden grid-cols-1 grid-rows-2 gap-6 md:grid">
        <University
          universityUrl="/img/landing/universities/harvard-university.png"
          className="opacity-0"
        />
        <University universityUrl="/img/landing/universities/harvard-university.png" />
      </div>
      <div className="hidden translate-y-[50px] grid-cols-1 grid-rows-2 gap-6 xl:grid">
        <University universityUrl="/img/landing/universities/boston-college-eagles.png" />
        <University universityUrl="/img/landing/universities/boston-university.png" />
      </div>
      <div className="hidden grid-cols-1 grid-rows-2 gap-6 xl:grid">
        <University universityUrl="/img/landing/universities/cal-university.png" />
        <University universityUrl="/img/landing/universities/columbia-university.png" />
      </div>
      <div className="hidden translate-y-[50px] grid-cols-1 grid-rows-2 gap-6 lg:grid">
        <University universityUrl="/img/landing/universities/fordham-rams.png" />
        <University universityUrl="/img/landing/universities/mit.png" />
      </div>
      <div className="hidden grid-cols-1 grid-rows-2 gap-6 lg:grid">
        <University universityUrl="/img/landing/universities/new-york-university.png" />
        <University universityUrl="/img/landing/universities/northeastern.png" />
      </div>
      <div className="grid translate-y-[50px] grid-cols-1 grid-rows-2 gap-6">
        <University universityUrl="/img/landing/universities/northwestern.png" />
        <University universityUrl="/img/landing/universities/stanford-university.png" />
      </div>
      <div className="grid grid-cols-1 grid-rows-2 gap-6">
        <University universityUrl="/img/landing/universities/ucla.png" />
        <University universityUrl="/img/landing/universities/uf-monogram.png" />
      </div>
      <div className="grid translate-y-[50px] grid-cols-1 grid-rows-2 gap-6">
        <University universityUrl="/img/landing/universities/university-of-chicago.png" />
        <University universityUrl="/img/landing/universities/university-of-pennsylvania.png" />
      </div>
      <div className="hidden grid-cols-1 grid-rows-2 gap-6 md:grid">
        <University
          universityUrl="/img/landing/universities/usc-trojans.png"
          className="opacity-0"
        />
        <University universityUrl="/img/landing/universities/usc-trojans.png" />
      </div>
    </div>
  );
}

type UniversityProps = {
  universityUrl: string;
  className?: string;
};
function University(props: UniversityProps) {
  const { universityUrl, className } = props;

  return (
    <div
      className={cn(
        'relative flex items-center justify-center rounded-md border-gray-100 bg-white p-2 shadow-outer-border',
        className,
      )}
    >
      <img src={universityUrl} alt="university" className="size-14 object-contain sm:size-20" />
      <Icon name="blue-checkmark" className="-bottom-3 -right-3 absolute h-10 w-10" />
    </div>
  );
}

function YouCouldEarnSection() {
  return (
    <div className="flex w-full flex-col items-start justify-center gap-6 bg-hostU-blue-gradient px-6 py-10 sm:items-center sm:py-20 md:px-8">
      <section className="flex w-full max-w-[1440px] flex-col items-start justify-between gap-8 text-white sm:flex-row sm:items-center">
        <div>
          <h2 className="font-bold text-xl sm:text-2xl lg:text-4xl xl:text-7xl">
            You can earn $2,700+
          </h2>
          <p className="font-normal text-lg text-white md:text-xl">
            *on an average stay of 3 months, at $900/month
          </p>
        </div>
        <Button
          variant="black"
          className="min-w-[266px] self-center rounded-full bg-black/50 text-base md:px-7 md:py-7 md:text-xl lg:text-2xl"
          asChild
        >
          <Link to="/app/listing/new">Sublet my place</Link>
        </Button>
      </section>
    </div>
  );
}

function WhatAreYouWaitingForSectionMobile() {
  return (
    <div className="relative flex w-full flex-col items-start justify-center overflow-hidden px-6 py-12 sm:hidden sm:items-center sm:py-20 md:px-8">
      <div className="flex w-full flex-col items-start text-blue-gray sm:items-center">
        <p className="font-semibold text-lg">What are you waiting for?</p>
        <p className="text-lg">Start getting paid today.</p>
        <div className="h-12" />
        <Button variant="blue-gray" className="rounded-full px-6 text-base">
          <Link to="/app/listing/new">Get started</Link>
        </Button>
        <WhatAreYouWaitingForSectionBg className="-right-[30%] -z-10 xs:-right-[26%] absolute top-14 bottom-0 h-full" />
      </div>
    </div>
  );
}

function WhatAreYouWaitingForSectionBg({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 300 233" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M80.37 108.301c-4.556 12.105-13.15 25.898-23.82 33.105-1.09.735-2.253 1.513-3.475 2.329-9.185 6.138-21.727 14.518-31.598 24.428-11.143 11.187-19.34 24.784-14.806 39.652 4.61 15.116 15.79 30.12 29.42 43.118 13.648 13.016 29.87 24.135 44.746 31.449 22.312 10.97 37.554 17.535 52.168 18.161 14.711.631 28.567-4.75 48.148-16.984q1.925-1.202 3.872-2.409c9.521-5.91 19.298-11.979 27.839-19.32 10.114-8.693 18.604-19.418 26.772-29.737q1.162-1.469 2.317-2.923a556 556 0 0 1 5.212-6.426c6.524-7.961 13.366-16.311 18.187-25.386 2.105-3.963 3.971-8.045 5.782-12.113l1.1-2.479c1.436-3.242 2.86-6.458 4.382-9.623 5.477-11.387 12.662-26.975 12.839-40.539.164-12.616-5.403-24.929-12.917-36.076-7.524-11.161-17.114-21.318-25.212-29.626-26.217-26.9-60.657-50.704-96.398-62.515-7.36-2.432-17.821-2.934-29.081-1.785-11.293 1.153-23.54 3.982-34.534 8.337C80.34 15.285 70.49 21.2 64.126 28.601c-6.415 7.46-9.305 16.47-6.154 26.72 1.706 5.553 4.583 10.27 7.73 14.552 1.573 2.14 3.224 4.186 4.833 6.17l.284.35c1.516 1.869 2.988 3.683 4.352 5.51 2.9 3.882 5.223 7.706 6.32 11.924 1.086 4.177.996 8.848-1.121 14.474Z"
        fill="url(#a)"
        stroke="#6EC4E7"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <g filter="url(#b)">
        <rect
          x="145.05"
          y="60.25"
          width="34.22"
          height="76.05"
          rx="17.11"
          transform="rotate(90 145.05 60.25)"
          fill="url(#c)"
        />
      </g>
      <path d="M122 69v14" fill="#fff" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
      <path
        d="m115 77 7 7 7-7"
        fill="#fff"
        stroke="#fff"
        strokeLinejoin="round"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="m98.899 66.281-18.385 6.691a2.82 2.82 0 0 0-1.68 3.603l3.355 9.218a2.82 2.82 0 0 0 3.603 1.68l18.385-6.691a2.82 2.82 0 0 0 1.68-3.603l-3.355-9.218a2.8 2.8 0 0 0-3.603-1.68m4.239 10.003c-1.15.538-1.764 1.809-1.553 3.048l-14.74 5.364a2.865 2.865 0 0 0-3.148-1.336l-2.154-5.917c1.15-.538 1.765-1.809 1.553-3.048l14.951-5.441c.589 1.042 1.768 1.57 2.955 1.378l2.163 5.943z"
        fill="#fff"
      />
      <path
        d="M85.59 76.15a.984.984 0 1 1-1.85.673.984.984 0 0 1 1.85-.673m15.351.755a.984.984 0 1 1-1.85.672.984.984 0 0 1 1.85-.672m-10.234-4.531a4.77 4.77 0 0 0-2.69 2.474 4.77 4.77 0 0 0-.166 3.65 4.77 4.77 0 0 0 2.474 2.689c1.153.538 2.462.6 3.65.167a4.77 4.77 0 0 0 2.69-2.474c.537-1.153.6-2.462.166-3.65a4.77 4.77 0 0 0-2.474-2.69 4.77 4.77 0 0 0-3.65-.166"
        fill="#fff"
      />
      <g filter="url(#d)">
        <rect
          x="30.95"
          y="136.222"
          width="34.222"
          height="76.049"
          rx="17.111"
          transform="rotate(-90 30.95 136.222)"
          fill="url(#e)"
        />
      </g>
      <path d="M84 112v14" fill="#fff" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
      <path
        d="m77 117 7-7 7 7"
        fill="#fff"
        stroke="#fff"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m64.86 119.526-1.806-1.785v-5.292a.52.52 0 0 0-.528-.521h-2.112a.52.52 0 0 0-.528.521v2.171l-3.475-3.444a1.065 1.065 0 0 0-1.49 0l-8.448 8.35a1.034 1.034 0 0 0 .153 1.607c.173.115.377.177.586.178h1.056v6.263c0 .276.111.542.31.738.198.195.466.305.746.305h3.697a.52.52 0 0 0 .528-.521v-5.219c0-.139.056-.271.155-.369a.53.53 0 0 1 .373-.153h3.168a.53.53 0 0 1 .374.153c.099.098.154.23.154.369v5.219a.52.52 0 0 0 .529.521h3.696c.28 0 .549-.11.747-.305a1.04 1.04 0 0 0 .31-.738v-6.263h1.055a1.06 1.06 0 0 0 .982-.643 1.03 1.03 0 0 0-.232-1.142"
        fill="#fff"
      />
      <defs>
        <linearGradient
          id="a"
          x1="146.567"
          y1="-1.194"
          x2="286.787"
          y2="117.599"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4BB5E2" />
          <stop offset=".022" stopColor="#4AB5E1" />
          <stop offset=".507" stopColor="#2FBDB6" />
          <stop offset="1" stopColor="#2EBDB5" />
        </linearGradient>
        <linearGradient
          id="c"
          x1="151.238"
          y1="62.897"
          x2="175.428"
          y2="63.65"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#63CC48" />
          <stop offset="1" stopColor="#A4D53E" />
        </linearGradient>
        <linearGradient
          id="e"
          x1="37.139"
          y1="138.869"
          x2="61.33"
          y2="139.623"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4BB5E2" />
          <stop offset="1" stopColor="#2EBDB5" />
        </linearGradient>
        <filter
          id="b"
          x="39"
          y="30.25"
          width="136.05"
          height="94.22"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="15" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_1076_790" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_1076_790" result="shape" />
        </filter>
        <filter
          id="d"
          x=".95"
          y="72"
          width="136.05"
          height="94.222"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="15" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0" />
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_1076_790" />
          <feBlend in="SourceGraphic" in2="effect1_dropShadow_1076_790" result="shape" />
        </filter>
      </defs>
    </svg>
  );
}
